import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../hooks/useApi';
import {
  PlusIcon, EditIcon, TrashIcon, CheckIcon, XIcon,
  RefreshIcon, AlertIcon, TrendUpIcon, EyeIcon, ZapIcon, CurrencyIcon,
} from '../../components/Icons';

const fmtData = (d) => d ? new Date(d).toLocaleDateString('pt-PT') : '—';
const fmt = (v) => new Intl.NumberFormat('pt-AO').format(v||0);

const PLANOS = [
  { id:'basico',   label:'Básico',   preco:'15.000 Kz/mês', desc:'Banner feed, 30 dias', cor:'#64748B' },
  { id:'standard', label:'Standard', preco:'35.000 Kz/mês', desc:'Destaque lista + banner', cor:'#2563EB' },
  { id:'premium',  label:'Premium',  preco:'75.000 Kz/mês', desc:'Topo lista + banner grande', cor:'#7C3AED' },
];

const POSICOES = [
  { id:'feed',     label:'Feed de produtos' },
  { id:'sidebar',  label:'Barra lateral' },
  { id:'banner',   label:'Banner topo página' },
  { id:'destaque', label:'Secção em destaque' },
];

const COR_PLANO = { basico:'badge-neutral', standard:'badge-info', premium:'badge-orange' };

const FORM_INICIAL = {
  titulo:'', empresa:'', subtitulo:'', descricao:'', imagem_url:'', link_destino:'',
  cor_primaria:'#F97316', cor_texto:'#FFFFFF',
  plano:'basico', posicao:'feed',
  data_inicio: new Date().toISOString().split('T')[0],
  data_fim: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
  preco_pago:'',
};

export default function PublicidadeAdmin() {
  const [campanhas,   setCampanhas]   = useState([]);
  const [carregando,  setCarregando]  = useState(true);
  const [erro,        setErro]        = useState(null);
  const [modal,        setModal]        = useState(false); // 'criar' | 'editar' | null
  const [editId,       setEditId]       = useState(null);
  const [form,         setForm]         = useState(FORM_INICIAL);
  const [saving,       setSaving]       = useState(false);
  const [erroForm,     setErroForm]     = useState('');
  const [imagemModo,   setImagemModo]   = useState('url');   // 'url' | 'upload'
  const [imagemFicheiro, setImagemFicheiro] = useState(null);
  const [imagemPreview,  setImagemPreview]  = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null);
    try {
      const d = await apiFetch('/admin/publicidade');
      setCampanhas(d.publicidade || []);
    } catch (e) { setErro(e.message); }
    finally { setCarregando(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const resetImagem = () => {
    setImagemModo('url'); setImagemFicheiro(null); setImagemPreview('');
  };

  const abrirCriar = () => {
    setForm(FORM_INICIAL); setEditId(null); setErroForm('');
    resetImagem(); setModal('criar');
  };
  const abrirEditar = (c) => {
    setForm({
      titulo: c.titulo||'', empresa: c.empresa||'', subtitulo: c.subtitulo||'',
      descricao: c.descricao||'', imagem_url: c.imagem_url||'', link_destino: c.link_destino||'',
      cor_primaria: c.cor_primaria||'#F97316', cor_texto: c.cor_texto||'#FFFFFF',
      plano: c.plano||'basico', posicao: c.posicao||'feed',
      data_inicio: c.data_inicio ? c.data_inicio.split('T')[0] : new Date().toISOString().split('T')[0],
      data_fim:    c.data_fim    ? c.data_fim.split('T')[0]    : '',
      preco_pago:  c.preco_pago||'',
    });
    setEditId(c.id); setErroForm('');
    resetImagem();
    setModal('editar');
  };

  const handleFicheiroChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImagemFicheiro(f);
    const url = URL.createObjectURL(f);
    setImagemPreview(url);
    setForm(p => ({ ...p, imagem_url: url })); // para o preview ao vivo
  };

  const guardar = async () => {
    if (!form.titulo || !form.empresa || !form.data_fim)
      return setErroForm('Título, empresa e data fim são obrigatórios');
    setSaving(true); setErroForm('');
    try {
      const endpoint = modal === 'criar' ? '/admin/publicidade' : `/admin/publicidade/${editId}`;
      const method   = modal === 'criar' ? 'POST' : 'PATCH';

      let payload;
      if (imagemModo === 'upload' && imagemFicheiro) {
        // Enviar como FormData para incluir o ficheiro
        const fd = new FormData();
        const campos = { ...form, preco_pago: parseFloat(form.preco_pago)||0 };
        Object.entries(campos).forEach(([k, v]) => {
          if (k !== 'imagem_url') fd.append(k, v ?? ''); // imagem_url será gerada pelo backend
        });
        fd.append('imagem', imagemFicheiro);
        payload = fd;
      } else {
        // JSON normal com URL
        payload = { ...form, preco_pago: parseFloat(form.preco_pago)||0 };
        if (imagemModo === 'url' && !form.imagem_url) payload.imagem_url = null;
      }

      await apiFetch(endpoint, method, payload);
      setModal(null);
      resetImagem();
      await carregar();
    } catch (e) { setErroForm(e.message); }
    finally { setSaving(false); }
  };

  const toggleAtivo = async (id, ativo) => {
    try {
      await apiFetch(`/admin/publicidade/${id}`, 'PATCH', { ativo: !ativo });
      await carregar();
    } catch (e) { alert(e.message); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('Tens a certeza que queres eliminar esta campanha?')) return;
    try {
      await apiFetch(`/admin/publicidade/${id}`, 'DELETE');
      await carregar();
    } catch (e) { alert(e.message); }
  };

  const inp = {
    width:'100%', padding:'9px 12px', border:'1px solid var(--border)',
    borderRadius:'8px', fontSize:'13px', fontFamily:'inherit',
    color:'var(--text-primary)', background:'var(--card)', outline:'none',
    boxSizing:'border-box',
  };
  const lbl = { fontSize:'12px', fontWeight:'500', color:'var(--text-secondary)', display:'block', marginBottom:'5px' };

  // Stats totais
  const totalImpressoes = campanhas.reduce((s,c) => s + (c.impressoes||0), 0);
  const totalCliques    = campanhas.reduce((s,c) => s + (c.cliques||0), 0);
  const totalReceita    = campanhas.reduce((s,c) => s + parseFloat(c.preco_pago||0), 0);
  const campanhasAtivas = campanhas.filter(c => c.ativo).length;

  return (
    <>
      {/* Modal */}
      {modal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}
          onClick={() => setModal(null)}>
          <div style={{background:'var(--card)',borderRadius:'16px',padding:'0',width:'100%',maxWidth:'560px',maxHeight:'90vh',overflow:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}
            onClick={e=>e.stopPropagation()}>
            {/* Modal header */}
            <div style={{padding:'20px 24px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{fontSize:'15px',fontWeight:'700'}}>{modal==='criar'?'Nova Campanha':'Editar Campanha'}</div>
              <button onClick={()=>setModal(null)} style={{background:'transparent',border:'none',cursor:'pointer',color:'var(--text-muted)',fontSize:'20px',lineHeight:1}}>×</button>
            </div>
            {/* Modal body */}
            <div style={{padding:'20px 24px',display:'flex',flexDirection:'column',gap:'14px'}}>
              {erroForm && (
                <div style={{background:'var(--danger-bg)',border:'1px solid var(--danger-border)',borderRadius:'8px',padding:'10px 14px',fontSize:'12px',color:'var(--danger)',display:'flex',gap:'6px',alignItems:'center'}}>
                  <AlertIcon size={13}/> {erroForm}
                </div>
              )}
              <div className="grid-2">
                <div style={{gridColumn:'1/-1'}}>
                  <label style={lbl}>Título do anúncio *</label>
                  <input style={inp} value={form.titulo} onChange={e=>setForm(p=>({...p,titulo:e.target.value}))} placeholder="Ex: Verão KiAluga — Aluga com desconto!" />
                </div>
                <div>
                  <label style={lbl}>Empresa / Marca *</label>
                  <input style={inp} value={form.empresa} onChange={e=>setForm(p=>({...p,empresa:e.target.value}))} placeholder="Ex: Coca-Cola Angola" />
                </div>
                <div>
                  <label style={lbl}>Subtítulo</label>
                  <input style={inp} value={form.subtitulo} onChange={e=>setForm(p=>({...p,subtitulo:e.target.value}))} placeholder="Tagline curta" />
                </div>
                <div style={{gridColumn:'1/-1'}}>
                  <label style={lbl}>Descrição</label>
                  <textarea style={{...inp,minHeight:'60px',resize:'vertical'}} value={form.descricao} onChange={e=>setForm(p=>({...p,descricao:e.target.value}))} placeholder="Texto do anúncio..." />
                </div>
                <div style={{gridColumn:'1/-1'}}>
                  <label style={lbl}>Imagem do anúncio</label>
                  {/* Toggle URL / Upload */}
                  <div style={{display:'flex',gap:'0',marginBottom:'8px',border:'1px solid var(--border)',borderRadius:'8px',overflow:'hidden',width:'fit-content'}}>
                    {['url','upload'].map(modo => (
                      <button key={modo} type="button"
                        onClick={() => { setImagemModo(modo); if(modo==='url'){setImagemFicheiro(null);setImagemPreview('');} }}
                        style={{
                          padding:'6px 14px', fontSize:'12px', fontFamily:'inherit', fontWeight:'600',
                          border:'none', cursor:'pointer', transition:'all 0.15s',
                          background: imagemModo===modo ? 'var(--orange)' : 'var(--card)',
                          color: imagemModo===modo ? '#fff' : 'var(--text-muted)',
                        }}>
                        {modo === 'url' ? '🔗 URL' : '📁 Upload'}
                      </button>
                    ))}
                  </div>

                  {imagemModo === 'url' ? (
                    <input style={inp} value={form.imagem_url} onChange={e=>setForm(p=>({...p,imagem_url:e.target.value}))} placeholder="https://exemplo.com/imagem.png" />
                  ) : (
                    <div>
                      <label
                        htmlFor="pub-imagem-upload"
                        style={{
                          display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                          gap:'8px', padding:'20px', border:'2px dashed var(--border)', borderRadius:'10px',
                          cursor:'pointer', transition:'all 0.15s', background:'var(--surface)',
                          ...(imagemPreview ? { borderColor:'var(--orange)', background:'#FFF7ED' } : {}),
                        }}
                        onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor='var(--orange)';e.currentTarget.style.background='#FFF7ED';}}
                        onDragLeave={e=>{if(!imagemPreview){e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.background='var(--surface)';}}}
                        onDrop={e=>{
                          e.preventDefault();
                          const f = e.dataTransfer.files?.[0];
                          if (f && /image\/(jpeg|jpg|png|webp)/.test(f.type)) {
                            setImagemFicheiro(f);
                            const url = URL.createObjectURL(f);
                            setImagemPreview(url);
                            setForm(p=>({...p,imagem_url:url}));
                          }
                        }}
                      >
                        {imagemPreview ? (
                          <>
                            <img src={imagemPreview} alt="preview" style={{width:'80px',height:'60px',objectFit:'cover',borderRadius:'8px',boxShadow:'0 2px 8px rgba(0,0,0,0.1)'}} />
                            <div style={{fontSize:'11px',color:'var(--orange)',fontWeight:'600'}}>{imagemFicheiro?.name}</div>
                            <div style={{fontSize:'10px',color:'var(--text-muted)'}}>Clica para substituir</div>
                          </>
                        ) : (
                          <>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                            </svg>
                            <div style={{fontSize:'12px',fontWeight:'600',color:'var(--text-secondary)'}}>Arrasta ou clica para seleccionar</div>
                            <div style={{fontSize:'11px',color:'var(--text-muted)'}}>JPG, PNG, WEBP · máx. 5MB</div>
                          </>
                        )}
                      </label>
                      <input id="pub-imagem-upload" type="file" accept="image/jpeg,image/jpg,image/png,image/webp" style={{display:'none'}} onChange={handleFicheiroChange} />
                      {imagemPreview && (
                        <button type="button" onClick={()=>{setImagemFicheiro(null);setImagemPreview('');setForm(p=>({...p,imagem_url:''}));}}
                          style={{marginTop:'6px',fontSize:'11px',color:'var(--danger)',background:'transparent',border:'none',cursor:'pointer',fontFamily:'inherit'}}>
                          × Remover imagem
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div style={{gridColumn:'1/-1'}}>
                  <label style={lbl}>Link de destino</label>
                  <input style={inp} value={form.link_destino} onChange={e=>setForm(p=>({...p,link_destino:e.target.value}))} placeholder="https://..." />
                </div>
                <div>
                  <label style={lbl}>Cor de fundo</label>
                  <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                    <input type="color" value={form.cor_primaria} onChange={e=>setForm(p=>({...p,cor_primaria:e.target.value}))} style={{width:'44px',height:'36px',borderRadius:'6px',border:'1px solid var(--border)',cursor:'pointer',padding:'2px'}} />
                    <input style={{...inp,flex:1}} value={form.cor_primaria} onChange={e=>setForm(p=>({...p,cor_primaria:e.target.value}))} placeholder="#F97316" />
                  </div>
                </div>
                <div>
                  <label style={lbl}>Cor do texto</label>
                  <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                    <input type="color" value={form.cor_texto} onChange={e=>setForm(p=>({...p,cor_texto:e.target.value}))} style={{width:'44px',height:'36px',borderRadius:'6px',border:'1px solid var(--border)',cursor:'pointer',padding:'2px'}} />
                    <input style={{...inp,flex:1}} value={form.cor_texto} onChange={e=>setForm(p=>({...p,cor_texto:e.target.value}))} placeholder="#FFFFFF" />
                  </div>
                </div>
                <div>
                  <label style={lbl}>Plano</label>
                  <select style={inp} value={form.plano} onChange={e=>setForm(p=>({...p,plano:e.target.value}))}>
                    {PLANOS.map(pl=><option key={pl.id} value={pl.id}>{pl.label} — {pl.preco}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Posição</label>
                  <select style={inp} value={form.posicao} onChange={e=>setForm(p=>({...p,posicao:e.target.value}))}>
                    {POSICOES.map(po=><option key={po.id} value={po.id}>{po.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Data início</label>
                  <input style={inp} type="date" value={form.data_inicio} onChange={e=>setForm(p=>({...p,data_inicio:e.target.value}))} />
                </div>
                <div>
                  <label style={lbl}>Data fim *</label>
                  <input style={inp} type="date" value={form.data_fim} onChange={e=>setForm(p=>({...p,data_fim:e.target.value}))} />
                </div>
                <div style={{gridColumn:'1/-1'}}>
                  <label style={lbl}>Valor pago (Kz)</label>
                  <input style={inp} type="number" min="0" value={form.preco_pago} onChange={e=>setForm(p=>({...p,preco_pago:e.target.value}))} placeholder="0" />
                </div>
              </div>
              {/* Preview */}
              {form.titulo && (
                <div style={{borderRadius:'12px',overflow:'hidden',border:'1px solid var(--border)'}}>
                  <div style={{fontSize:'11px',color:'var(--text-muted)',padding:'8px 12px',background:'var(--surface)',borderBottom:'1px solid var(--border)',fontWeight:'600',textTransform:'uppercase',letterSpacing:'0.4px'}}>
                    Pré-visualização
                  </div>
                  <div style={{padding:'16px',background:form.cor_primaria,display:'flex',alignItems:'center',gap:'12px'}}>
                    {(imagemPreview || form.imagem_url) && <img src={imagemPreview || form.imagem_url} alt="" style={{width:'48px',height:'48px',borderRadius:'8px',objectFit:'cover',flexShrink:0}} onError={e=>e.target.style.display='none'} />}
                    <div>
                      <div style={{fontSize:'13px',fontWeight:'700',color:form.cor_texto}}>{form.titulo}</div>
                      {form.subtitulo && <div style={{fontSize:'11px',color:form.cor_texto,opacity:0.8,marginTop:'2px'}}>{form.subtitulo}</div>}
                      <div style={{fontSize:'11px',color:form.cor_texto,opacity:0.6,marginTop:'2px'}}>{form.empresa}</div>
                    </div>
                    {form.link_destino && (
                      <div style={{marginLeft:'auto',background:'rgba(255,255,255,0.25)',borderRadius:'6px',padding:'5px 10px',fontSize:'11px',color:form.cor_texto,fontWeight:'600',flexShrink:0,cursor:'pointer'}}>
                        Saiba mais →
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* Modal footer */}
            <div style={{padding:'16px 24px',borderTop:'1px solid var(--border)',display:'flex',justifyContent:'flex-end',gap:'8px'}}>
              <button className="btn btn-outline" onClick={()=>setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardar} disabled={saving} style={{display:'flex',alignItems:'center',gap:'6px'}}>
                <CheckIcon size={13}/> {saving ? 'A guardar...' : modal==='criar' ? 'Criar campanha' : 'Guardar alterações'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Topbar */}
      <div className="topbar">
        <div>
          <div className="topbar-title">Área de Publicidade</div>
          <div className="topbar-sub">{campanhas.length} campanhas · {campanhasAtivas} activas</div>
        </div>
        <div className="topbar-actions">
          <button className="btn btn-ghost" onClick={carregar} style={{display:'flex',alignItems:'center',gap:'6px'}}>
            <RefreshIcon size={14}/> Actualizar
          </button>
          <button className="btn btn-primary" onClick={abrirCriar} style={{display:'flex',alignItems:'center',gap:'6px'}}>
            <PlusIcon size={14}/> Nova campanha
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Stats */}
        <div className="stats-grid" style={{marginBottom:'20px'}}>
          <div className="stat-card" style={{position:'relative',overflow:'hidden'}}>
            <div style={{position:'absolute',right:'-12px',bottom:'-12px',width:'80px',height:'80px',borderRadius:'50%',background:'rgba(249,115,22,0.06)'}}/>
            <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:'10px'}}>
              <div className="stat-icon" style={{background:'#FFF7ED'}}><ZapIcon size={18} color="#F97316"/></div>
              {campanhasAtivas > 0 && <span className="badge badge-success" style={{fontSize:'10px'}}>{campanhasAtivas} activas</span>}
            </div>
            <div className="stat-value">{campanhas.length}</div>
            <div className="stat-label">Total campanhas</div>
          </div>
          <div className="stat-card" style={{position:'relative',overflow:'hidden'}}>
            <div style={{position:'absolute',right:'-12px',bottom:'-12px',width:'80px',height:'80px',borderRadius:'50%',background:'rgba(37,99,235,0.06)'}}/>
            <div style={{display:'flex',alignItems:'flex-start',marginBottom:'10px'}}>
              <div className="stat-icon" style={{background:'#EFF6FF'}}><EyeIcon size={18} color="#2563EB"/></div>
            </div>
            <div className="stat-value" style={{fontSize:'18px'}}>{fmt(totalImpressoes)}</div>
            <div className="stat-label">Impressões totais</div>
          </div>
          <div className="stat-card" style={{position:'relative',overflow:'hidden'}}>
            <div style={{position:'absolute',right:'-12px',bottom:'-12px',width:'80px',height:'80px',borderRadius:'50%',background:'rgba(124,58,237,0.06)'}}/>
            <div style={{display:'flex',alignItems:'flex-start',marginBottom:'10px'}}>
              <div className="stat-icon" style={{background:'#F5F3FF'}}><TrendUpIcon size={18} color="#7C3AED"/></div>
            </div>
            <div className="stat-value">{fmt(totalCliques)}</div>
            <div className="stat-label">Cliques totais</div>
            <div style={{fontSize:'11px',color:'var(--text-muted)',marginTop:'2px'}}>
              CTR: {totalImpressoes > 0 ? (totalCliques/totalImpressoes*100).toFixed(1) : 0}%
            </div>
          </div>
          <div className="stat-card" style={{position:'relative',overflow:'hidden'}}>
            <div style={{position:'absolute',right:'-12px',bottom:'-12px',width:'80px',height:'80px',borderRadius:'50%',background:'rgba(22,163,74,0.06)'}}/>
            <div style={{display:'flex',alignItems:'flex-start',marginBottom:'10px'}}>
              <div className="stat-icon" style={{background:'#F0FDF4'}}><CurrencyIcon size={18} color="#16A34A"/></div>
            </div>
            <div className="stat-value" style={{fontSize:'16px'}}>{fmt(totalReceita)} Kz</div>
            <div className="stat-label">Receita publicidade</div>
          </div>
        </div>

        {/* Planos */}
        <div className="card" style={{marginBottom:'16px',padding:'16px 20px'}}>
          <div style={{fontSize:'13px',fontWeight:'600',marginBottom:'12px',display:'flex',alignItems:'center',gap:'8px'}}>
            <ZapIcon size={14} color='var(--orange)'/> Planos disponíveis
          </div>
          <div className="grid-3">
            {PLANOS.map(pl => (
              <div key={pl.id} style={{
                padding:'14px',borderRadius:'12px',
                border:`2px solid ${pl.id==='premium'?'#7C3AED':pl.id==='standard'?'#2563EB':'var(--border)'}`,
                background:pl.id==='premium'?'#F5F3FF':pl.id==='standard'?'#EFF6FF':'var(--surface)',
              }}>
                <div style={{fontSize:'12px',fontWeight:'700',color:pl.cor,marginBottom:'4px'}}>{pl.label}</div>
                <div style={{fontSize:'16px',fontWeight:'800',fontFamily:'Syne,sans-serif',color:'var(--text-primary)',marginBottom:'4px'}}>{pl.preco}</div>
                <div style={{fontSize:'11px',color:'var(--text-muted)'}}>{pl.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabela de campanhas */}
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          {erro && (
            <div style={{padding:'12px 20px',background:'var(--danger-bg)',borderBottom:'1px solid var(--danger-border)',color:'var(--danger)',fontSize:'13px',display:'flex',gap:'8px',alignItems:'center'}}>
              <AlertIcon size={14}/> {erro}
            </div>
          )}
          {carregando ? (
            <div style={{padding:'40px',textAlign:'center',color:'var(--text-muted)'}}>
              <RefreshIcon size={20}/><div style={{marginTop:'8px',fontSize:'13px'}}>A carregar campanhas…</div>
            </div>
          ) : campanhas.length === 0 ? (
            <div style={{padding:'60px 20px',textAlign:'center'}}>
              <svg width="80" height="60" viewBox="0 0 80 60" fill="none">
                <rect x="10" y="15" width="60" height="35" rx="8" fill="#F1F5F9" stroke="#E2E8F0" strokeWidth="1.5"/>
                <rect x="18" y="22" width="28" height="5" rx="2.5" fill="#CBD5E1"/>
                <rect x="18" y="30" width="20" height="3" rx="1.5" fill="#E2E8F0"/>
                <circle cx="58" cy="20" r="10" fill="#FFF7ED" stroke="#FED7AA" strokeWidth="1.5"/>
                <path d="M58 15v6M58 23h.01" stroke="#F97316" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <div style={{fontSize:'14px',fontWeight:'600',color:'var(--text-secondary)',marginTop:'10px'}}>Sem campanhas ainda</div>
              <div style={{fontSize:'12px',color:'var(--text-muted)',marginTop:'4px',marginBottom:'16px'}}>Cria a primeira campanha publicitária da KiAluga</div>
              <button className="btn btn-primary btn-sm" onClick={abrirCriar} style={{display:'inline-flex',gap:'5px'}}>
                <PlusIcon size={12}/> Nova campanha
              </button>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Anúncio</th>
                    <th>Plano</th>
                    <th>Período</th>
                    <th>Impressões</th>
                    <th>Cliques / CTR</th>
                    <th>Receita</th>
                    <th>Estado</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {campanhas.map(c => {
                    const hoje = new Date().toISOString().split('T')[0];
                    const expirado = c.data_fim && c.data_fim.split('T')[0] < hoje;
                    const ctr = c.impressoes > 0 ? (c.cliques/c.impressoes*100).toFixed(1) : '0.0';
                    return (
                      <tr key={c.id}>
                        <td>
                          <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                            {c.imagem_url
                              ? <img src={c.imagem_url} alt="" style={{width:'36px',height:'36px',borderRadius:'8px',objectFit:'cover',flexShrink:0}} onError={e=>e.target.style.display='none'} />
                              : <div style={{width:'36px',height:'36px',borderRadius:'8px',background:c.cor_primaria||'#F97316',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                  <ZapIcon size={16} color={c.cor_texto||'#fff'}/>
                                </div>
                            }
                            <div>
                              <div style={{fontWeight:'600',fontSize:'12px',color:'var(--text-primary)'}}>{c.titulo}</div>
                              <div style={{fontSize:'10px',color:'var(--text-muted)'}}>{c.empresa}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${COR_PLANO[c.plano]||'badge-neutral'}`} style={{textTransform:'capitalize'}}>
                            {c.plano}
                          </span>
                        </td>
                        <td style={{fontSize:'11px',color:'var(--text-muted)',whiteSpace:'nowrap'}}>
                          <div>{fmtData(c.data_inicio)}</div>
                          <div>→ {fmtData(c.data_fim)}</div>
                          {expirado && <div style={{color:'var(--danger)',fontWeight:'600',fontSize:'10px'}}>Expirado</div>}
                        </td>
                        <td style={{fontSize:'12px',fontWeight:'600'}}>{fmt(c.impressoes)}</td>
                        <td>
                          <div style={{fontSize:'12px',fontWeight:'600'}}>{fmt(c.cliques)}</div>
                          <div style={{fontSize:'10px',color:'var(--text-muted)'}}>{ctr}% CTR</div>
                        </td>
                        <td style={{fontSize:'12px',fontWeight:'600',color:'var(--success)',whiteSpace:'nowrap'}}>{fmt(c.preco_pago)} Kz</td>
                        <td>
                          <span className={`badge ${c.ativo && !expirado ? 'badge-success' : 'badge-neutral'}`}>
                            <span style={{width:'5px',height:'5px',borderRadius:'50%',background: c.ativo&&!expirado ? 'var(--success)' : '#94A3B8', display:'inline-block',marginRight:'5px'}}/>
                            {expirado ? 'Expirado' : c.ativo ? 'Activo' : 'Pausado'}
                          </span>
                        </td>
                        <td>
                          <div style={{display:'flex',gap:'4px',alignItems:'center'}}>
                            <button
                              onClick={() => toggleAtivo(c.id, c.ativo)}
                              style={{padding:'5px 10px',borderRadius:'20px',border:'none',cursor:'pointer',fontSize:'11px',fontFamily:'inherit',fontWeight:'700',transition:'all 0.15s',
                                background: c.ativo ? '#FEF2F2' : '#DCFCE7',
                                color: c.ativo ? '#DC2626' : '#15803D',
                              }}
                              title={c.ativo ? 'Pausar' : 'Activar'}
                            >
                              {c.ativo ? <XIcon size={11}/> : <CheckIcon size={11}/>}
                            </button>
                            <button
                              onClick={() => abrirEditar(c)}
                              style={{padding:'5px 10px',borderRadius:'20px',border:'1px solid var(--border)',background:'transparent',cursor:'pointer',fontSize:'11px',fontFamily:'inherit',fontWeight:'600',transition:'all 0.15s',color:'var(--text-muted)',display:'flex',alignItems:'center',gap:'3px'}}
                            >
                              <EditIcon size={11}/>
                            </button>
                            <button
                              onClick={() => eliminar(c.id)}
                              style={{padding:'5px 10px',borderRadius:'20px',border:'none',background:'transparent',cursor:'pointer',fontSize:'11px',fontFamily:'inherit',fontWeight:'700',transition:'all 0.15s',color:'var(--danger)',display:'flex',alignItems:'center',gap:'3px'}}
                            >
                              <TrashIcon size={11}/>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
